import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import useLocalStorage from '../../../hooks/useLocalStorage'
import { GetCartResponse } from '../../../types/types'
import { LOCALE_STORAGE_KEYS } from '../../../utils/enums'
import { AppContext } from '../../../utils/appProvider'
import type { ReservationDeviceStorage } from '../../../utils/reservationDeviceId'

export type CartReservationProgressState = 'hidden' | 'loading' | 'ready'

type UseCartReservationProgressOptions = {
	hideProgressOnError?: boolean
}

const useCartReservationProgress = (options?: UseCartReservationProgressOptions) => {
	const { hideProgressOnError = false } = options ?? {}
	const { apiBrowser } = useContext(AppContext)
	const { storedValue: cartReservationDeviceData, loadingPersistentData } = useLocalStorage<ReservationDeviceStorage>(LOCALE_STORAGE_KEYS.DEVICE_ID)
	const isCartFetchEnabled = Boolean(cartReservationDeviceData?.deviceID) && cartReservationDeviceData?.cartActive !== false
	const [cartData, setCartData] = useState<GetCartResponse | null>(null)
	const [isFetchingCart, setIsFetchingCart] = useState(true)
	const [loadingError, setLoadingError] = useState(false)
	const [tryAgain, setTryAgain] = useState(false)

	const fetchCart = useCallback(async () => {
		if (!isCartFetchEnabled) {
			setIsFetchingCart(false)
			setLoadingError(false)
			setCartData(null)
			return
		}

		const { deviceID } = cartReservationDeviceData!

		setIsFetchingCart(true)
		setLoadingError(false)

		try {
			const response = await apiBrowser.b2c.getCart(deviceID)
			setCartData(response)
		} catch {
			setLoadingError(true)
		} finally {
			setIsFetchingCart(false)
		}
	}, [apiBrowser.b2c, cartReservationDeviceData, isCartFetchEnabled])

	useEffect(() => {
		if (loadingPersistentData) {
			return
		}

		fetchCart()
	}, [fetchCart, loadingPersistentData, tryAgain])

	const isReady = isCartFetchEnabled && cartData !== null && !isFetchingCart && !loadingPersistentData && !loadingError

	const progressState = useMemo<CartReservationProgressState>(() => {
		if (!isCartFetchEnabled) {
			return 'hidden'
		}

		if (loadingError) {
			return hideProgressOnError ? 'hidden' : 'loading'
		}

		if (isReady) {
			return 'ready'
		}

		return 'loading'
	}, [hideProgressOnError, isCartFetchEnabled, isReady, loadingError])

	return {
		cartData,
		expiresIn: cartData?.cart.expiresIn,
		isFetchingCart,
		isReady,
		progressState,
		loadingError,
		loadingPersistentData,
		fetchCart,
		retryFetchCart: () => setTryAgain((current) => !current)
	}
}

export default useCartReservationProgress
